import filesize from 'filesize';
import moment from 'moment-timezone';
import Logger from '../lib/logger';

const logger = Logger('utils/index');

export function singleLineString(strings, ...values) {
  // Interweave the strings with the
  // substitution vars first.
  let output = '';
  for (let i = 0; i < values.length; i += 1) {
    output += strings[i] + values[i];
  }
  output += strings[values.length];

  // Split on newlines.
  const lines = output.split(/(?:\r\n|\n|\r)/);

  // Rip out the leading whitespace.
  return lines
    .map(line => line.replace(/^\s+/gm, ''))
    .join(' ')
    .trim();
}

// Takes a number (of bytes), and returns it as a string in KiB, MiB, etc.
export const formatBytes = (num, precision = 2) => {
  if (num === '' || num === undefined) {
    return '';
  }

  return filesize(num, {
    locale: true,
    round: precision,
    standard: 'iec',
  });
};

// A constant used in formatNumber
const JEDEC_TO_CUSTOM_SYMBOLS = {
  B: ' ',
  kB: 'K',
  MB: 'M',
  GB: 'G',
  TB: 'T',
  PB: 'P',
  EB: 'E',
  ZB: 'Z',
  YB: 'Y',
};
// Takes a number (not byte number), and returns it as a string
// with JEDEC unit abbreviations mapped to custom ones
export const formatNumber = (num, precision = 2) => {
  if (!num && num !== 0) {
    return '';
  }

  return filesize(num, {
    base: 10,
    round: precision,
    symbols: JEDEC_TO_CUSTOM_SYMBOLS,
  });
};

// Takes a string of decimal digits.
// Returns the string's truncation to 'maxLength' digits, and a units suffix.
export const formatNumberCustom = (inputStr, maxLength = 9) => {
  const sizeNames = ['K', 'M', 'G', 'T', 'P'];

  const out = {
    value: inputStr || '',
    suffix: '',
  };

  for (let i = 0; i < sizeNames.length; i += 1) {
    if (out.value.length <= maxLength) {
      return out;
    }

    out.value = out.value.slice(0, -3);
    out.suffix = sizeNames[i];
  }

  return out;
};

export const composePath = (event, nodes = []) => {
  if (nodes && nodes.length > 0) {
    const element = nodes[nodes.length - 1];

    if (element.parentElement.tagName === 'HTML') {
      return nodes.concat(element.parentElement);
    }

    return composePath(event, nodes.concat([element.parentElement]));
  }

  return composePath(event, [event.target]);
};

export const composedPath = (event) => {
  if (event.composedPath && typeof event.composedPath === 'function') {
    return event.composedPath();
  }

  if (event.path && Array.isArray(event.path)) {
    return event.path;
  }

  return composePath(event);
};

/*
Returns true if `evt.target` is inside `domElt`.
Can be replaced with evt.composedPath() check for everything except IE.
*/
export const isInside = (evt, domElt) => {
  if (!evt) {
    return false;
  }

  let current = evt.target;

  while (current) {
    if (current === domElt) {
      return true;
    }
    current = current.parentNode;
  }

  return false;
};

// Returns a formatted date string in UTC if guessTz is falsy,
// or in the user's timezone if guessTz is truthy
export const formatDate = ({
  date,
  formatStr,
  guessTz = false,
}) => {
  let momentObj;

  try {
    momentObj = moment.utc(date);
  } catch (err) {
    logger.error('formatDate received invalid date', err);

    return '';
  }

  if (!momentObj.isValid()) {
    return '';
  }

  if (guessTz) {
    return momentObj
      .tz(moment.tz.guess())
      .format(formatStr);
  }

  return momentObj.format(formatStr);
};

export const formatDateUserTz = date => formatDate({
  date,
  formatStr: 'lll z',
  guessTz: true,
});

const NO_EPS_TEXT = 'Not available';

// Returns events-per-second as a string
export const getEps = ({
  logsTotal,
  windowEnd,
  windowStart,
}) => {
  let end;
  let start;

  try {
    end = moment.utc(windowEnd);
    start = moment.utc(windowStart);
  } catch (err) {
    logger.error(
      'Sensor support EventsPerSecond received invalid time window',
      err
    );
    return NO_EPS_TEXT;
  }

  const delta = end.diff(start, 'seconds');

  const eps = Math.round((logsTotal || 0) / delta);

  return eps;
};


export const isNumber = (num) => /^-?[\d.]+(?:e-?\d+)?$/.test(num);